import type { SignupApiError } from './api';
import type { CodeOptionSource } from './types';

// Words for the signup API's error codes (apiv2/signup.openapi.yaml `ErrorCode`) and the page's own
// (api.ts CLIENT_ERROR_CODES). The API's `detail` is already a sentence for the client, so it wins;
// these are what the page says when an answer has none. signupContract.test.js checks every code of
// the contract has words here.

export const ERROR_WORDS: Record<string, string> = {
    invalid: 'Some of the details are not valid. Check the highlighted fields.',
    rate_limited: 'Too many attempts. Wait a moment, then try again.',
    signup_disabled: 'Signing up is not available right now. Please try again later.',
    signup_secret_missing: 'Signing up is not available right now. Please try again later.',
    invalid_code: 'This link is not valid. Open the complete link from your invitation e-mail.',
    claimed_elsewhere: 'This link is in use in another browser.',
    invite_used: 'Your account has already been created. Sign in to your dashboard.',
    invite_revoked: 'This invitation is no longer valid. Ask your Curated Planet contact for a new link.',
    invite_expired: 'This invitation has expired. Ask your Curated Planet contact for a new link.',
    session_missing: 'Your session has ended. Open the link again to continue.',
    session_invalid: 'Your session has ended. Open the link again to continue.',
    session_expired: 'Your session has expired. Open the link again to continue.',
    session_revoked: 'This browser no longer holds the link.',
    unknown_restech: 'This booking system is not supported for self sign-up.',
    restech_not_allowed: 'Your invitation is for a different booking system.',
    signup_unavailable: 'Signing up is not available right now. Please try again later.',
    vendor_busy: 'Your booking system is busy. Try again in a minute.',
    vendor_unreachable: 'Your booking system did not answer. Try again.',
    credential_rejected: 'Your booking system did not accept these details. Check them and try again.',
    no_companies: 'These details do not give access to any company.',
    account_not_found: 'These details do not give access to that company.',
    not_found: 'Not found.',
    artifact_unavailable: 'The image could not be loaded. Try again.',
    code_edit_not_allowed: 'The web address of your dashboard cannot be changed.',
    invite_bound: 'Your invitation already has an e-mail address; no code is needed.',
    email_send_failed: 'The e-mail could not be sent. Try again.',
    code_invalid: 'That code is not right.',
    code_expired: 'The code has expired. Send a new one.',
    too_many_attempts: 'Too many wrong codes. Send a new one.',
    email_not_verified: 'Confirm your e-mail address first.',
    account_not_confirmed: 'Confirm your company first.',
    already_onboarded: "This account is already connected to TourOptima — we've let your Curated Planet contact know.",
    code_not_offered: 'That web address is not one of the ones offered for your dashboard. Choose one of them.',
    returning_client: "We need to finish setting up this account with you. We've let your Curated Planet contact know.",
    code_unavailable: 'We could not find a free web address for your dashboard. Please contact Curated Planet.',
    code_taken: 'This web address was taken in the meantime. Choose another one.',
    bad_candidate: 'That logo is no longer available. Choose another one.',
    not_connected: 'Your booking-system account is not connected any more. Connect it again.',
    oauth_not_supported: 'This booking system cannot be connected this way. Enter its access details instead.',
    account_ambiguous: 'We could not tell which company these details belong to. Connect with your booking system instead.',
    bokun_scope_missing: 'Your booking system did not give TourOptima all the access it needs. Connect again and allow everything it asks for.',
    domain_mismatch: 'Your booking system connected a different company than the one you entered. Check it and connect again.',
    // The page's own:
    network: 'We could not reach TourOptima. Check your connection and try again.',
    disabled: 'Signing up is not available right now. Please try again later.',
    server_error: 'Something went wrong on our side. Try again in a moment.',
};

const GENERIC = ERROR_WORDS.server_error;

// The kill switch answers `404 {detail: "Not found.", code: "signup_disabled"}`: that detail says
// nothing to a client.
const OWN_WORDS_ONLY = ['signup_disabled', 'disabled', 'network', 'server_error'];

function wait(seconds: number | null): string {
    if (seconds == null || seconds <= 0) {
        return '';
    }
    if (seconds < 90) {
        return ` Try again in ${seconds} second${seconds === 1 ? '' : 's'}.`;
    }
    const minutes = Math.ceil(seconds / 60);
    return ` Try again in ${minutes} minutes.`;
}

/** The Confirm screen's warning when a test or demo platform lets a company already on TourOptima sign up again. */
export const DUPLICATE_ALLOWED_WORDS =
    'This company is already on TourOptima. This test platform lets you continue anyway: you will create a second ' +
    'TourOptima account for the same company.';

/** The Confirm screen's words when a create came back `409 code_taken` with fresh `code_options`. */
export const CODE_TAKEN_PICK_WORDS = 'That address was just taken. Pick another one.';

// Captions of the tenant code options (the contract's `CodeOption.source`), by the booking system's display name.
// signupContract.test.js checks every source of the contract has a caption here.
export const CODE_OPTION_WORDS: Record<CodeOptionSource, (name: string) => string> = {
    handle: (name) => `Your ${name} name`,
    name: () => 'Your company name',
    website: () => 'From your website',
    email: () => 'From your e-mail domain',
    short: () => 'Shorter',
    place: () => 'With your country code',
    initials: () => 'Initials',
    numbered: () => 'Numbered',
};

// Why an option cannot be picked (the contract's `CodeOption.reason`).
export const CODE_OPTION_REASON_WORDS: Record<string, string> = {
    taken: 'Taken: another TourOptima account already uses this address',
    reserved: "Reserved: this address can't be used",
    invalid: "This name can't be used as an address",
};

/** The caption of a code option (an unknown source: none). */
export function codeOptionText(source: string, displayName: string): string {
    const words = Object.prototype.hasOwnProperty.call(CODE_OPTION_WORDS, source)
        ? CODE_OPTION_WORDS[source as CodeOptionSource] : undefined;
    return words ? words(displayName) : '';
}

/** Why a code option is disabled (an unknown or missing reason: generic words). */
export function codeOptionReasonText(reason: string | null | undefined): string {
    return (reason && Object.prototype.hasOwnProperty.call(CODE_OPTION_REASON_WORDS, reason) && CODE_OPTION_REASON_WORDS[reason])
        || 'This address is not available.';
}

/** What to tell the client about a failed call. */
export function errorText(error: unknown): string {
    const e = error as SignupApiError;
    if (!e || typeof e.code !== 'string') {
        return GENERIC;
    }
    const words = (!OWN_WORDS_ONLY.includes(e.code) && e.detail) || ERROR_WORDS[e.code] || e.detail || GENERIC;
    if (e.code === 'rate_limited') {
        return `${words}${wait(e.retryAfter)}`;
    }
    return words;
}

// Words for the `reason` of `?<restech>=error&reason=<code>` (the contract's `ConnectReturnReason`; the page's own
// `not_connected` for "connected" with nothing held), by the booking system's display name. signupContract.test.js
// checks every reason of the contract has words here.
export const CONNECT_REASON_WORDS: Record<string, (name: string) => string> = {
    state_invalid: (name) => `Connecting to ${name} did not finish. Start again.`,
    state_expired: (name) => `Connecting to ${name} took too long. Start again.`,
    state_used: (name) => `That connection to ${name} was already used. Start again.`,
    browser_mismatch: (name) => `Finish connecting to ${name} in the browser where you started, or start again here.`,
    label_invalid: (name) => `We could not find that ${name} account. Check it and connect again.`,
    label_mismatch: (name) => `${name} answered for a different account than the one you entered. Check it and connect again.`,
    signature_invalid: (name) => `We could not confirm the answer came from ${name}. Start again.`,
    access_denied: (name) => `${name} did not give TourOptima access. Connect again and allow access to continue.`,
    code_missing: (name) => `${name} did not finish the connection. Start again.`,
    invite_unusable: () => 'Your sign-up session ended while you were connecting. Open the link from your invitation again.',
    already_connected: (name) => `This ${name} account is already connected to TourOptima. Your Curated Planet contact can help.`,
    not_supported: (name) => `${name} cannot be connected this way. Enter its access details instead.`,
    not_connected: (name) => `Your ${name} account is not connected any more. Connect it again.`,
    unavailable: (name) => `Connecting to ${name} is not available right now. Try again in a moment.`,
    signup_disabled: () => ERROR_WORDS.signup_disabled,
    signup_secret_missing: () => ERROR_WORDS.signup_secret_missing,
    marketplace_capped: (name) => `We are not taking new ${name} installs right now. Try again tomorrow, or ask your Curated Planet contact for an invitation.`,
    credential_rejected: (name) => `${name} did not accept the connection. Try again.`,
    vendor_unreachable: (name) => `We could not reach ${name}. Try again in a moment.`,
    vendor_busy: (name) => `${name} is busy right now. Try again in a minute.`,
    signup_unavailable: (name) => `Connecting to ${name} is not available right now. Try again later.`,
    bokun_scope_missing: (name) => `${name} did not give TourOptima all the access it needs. Connect again and allow everything it asks for.`,
    domain_mismatch: (name) => `${name} connected a different company than the one you entered. Check it and connect again.`,
};

/** What to tell the client when connecting came back with `reason` (a code; unknown or none: generic words). */
export function connectReturnText(reason: string | null | undefined, displayName: string): string {
    const words = reason && Object.prototype.hasOwnProperty.call(CONNECT_REASON_WORDS, reason) ? CONNECT_REASON_WORDS[reason] : undefined;
    return words ? words(displayName) : `Connecting to ${displayName} did not work. Try again.`;
}

/** "17 October 2026, 14:05" in the browser's locale; the raw value when it does not parse. */
export function formatWhen(iso: string | null | undefined): string {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleString(undefined, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
